import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { useFaust, PROJECT_DIR } from "../contexts/FaustContext";
import { safeStorage } from "../lib/utils";
import VERSION from "../version";

/**
 * Initializes the Faust compiler, BrowserFS, and loads the project.
 * Should be called once at the top-level App component.
 */
export function useFaustInit() {
    const {
        dispatch,
        compilerRef,
        libFaustRef,
        svgDiagramsRef,
        bfsRef,
        compileOptionsRef,
        audioEnvRef,
    } = useFaust();

    const initRef = useRef(false);

    useEffect(() => {
        if (initRef.current) return;
        initRef.current = true;

        (async () => {
            try {
                // Load Faust compiler
                const {
                    instantiateFaustModuleFromFile,
                    LibFaust,
                    FaustCompiler,
                    FaustSvgDiagrams,
                } = await import("@grame/faustwasm");

                const faustModule = await instantiateFaustModuleFromFile("faustwasm/libfaust-wasm.js");
                const libFaust = new LibFaust(faustModule);
                const faustCompiler = new FaustCompiler(libFaust);
                const faustSvgDiagrams = new FaustSvgDiagrams(faustCompiler);

                // Load primitives library
                const primResponse = await fetch("primitives.lib");
                const primText = await primResponse.text();
                libFaust.fs().writeFile("/usr/share/faust/primitives.lib", primText);

                compilerRef.current = faustCompiler;
                libFaustRef.current = libFaust;
                svgDiagramsRef.current = faustSvgDiagrams;

                // Initialize BrowserFS (IndexedDB-backed)
                const BrowserFS = await import("@zenfs/core");
                const { IndexedDB } = await import("@zenfs/dom");
                await BrowserFS.configureSingle({
                    backend: IndexedDB,
                    storeName: "FaustIDE" as any,
                });
                bfsRef.current = BrowserFS.promises;

                // Load project files into Emscripten FS
                const mfs = libFaust.fs();
                mfs.mkdir(PROJECT_DIR);
                let files = await BrowserFS.promises.readdir("/");
                files = files.filter((n: string) => n !== "." && n !== "..");

                if (compileOptionsRef.current.saveCode) {
                    await Promise.all(
                        files.map(async (filename: string) => {
                            const data = await BrowserFS.promises.readFile(filename);
                            mfs.writeFile(PROJECT_DIR + filename, new Uint8Array(data.buffer));
                        })
                    );
                } else {
                    await Promise.all(files.map((f: string) => BrowserFS.promises.unlink(f)));
                }

                // Initialize audio context
                const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)({ latencyHint: 0.00001 });
                audioEnvRef.current.audioCtx = audioCtx;
                audioEnvRef.current.outputEnabled = true;
                audioEnvRef.current.destination = audioCtx.destination;
                audioEnvRef.current.destination.channelCount = audioEnvRef.current.destination.maxChannelCount;
                audioEnvRef.current.destination.channelInterpretation = "discrete";

                // Unlock suspended audio context on user gesture
                if (audioCtx.state === "suspended") {
                    const unlock = () => {
                        audioCtx.resume().then(() => {
                            document.removeEventListener("touchstart", unlock);
                            document.removeEventListener("mousedown", unlock);
                            document.removeEventListener("keydown", unlock);
                        });
                    };
                    document.addEventListener("touchstart", unlock);
                    document.addEventListener("mousedown", unlock);
                    document.addEventListener("keydown", unlock);
                }

                // Create audio nodes
                if (!audioEnvRef.current.gainInput) {
                    audioEnvRef.current.gainInput = audioCtx.createGain();
                }
                if (!audioEnvRef.current.splitterInput) {
                    audioEnvRef.current.splitterInput = audioCtx.createChannelSplitter(2);
                }
                if (!audioEnvRef.current.analyserInput) {
                    audioEnvRef.current.analyserInput = audioCtx.createAnalyser();
                }
                if (!audioEnvRef.current.analyserOutput) {
                    audioEnvRef.current.analyserOutput = audioCtx.createAnalyser();
                }
                // Wire: gainInput -> splitterInput -> analyserInput
                audioEnvRef.current.gainInput.connect(audioEnvRef.current.splitterInput);
                audioEnvRef.current.splitterInput.connect(audioEnvRef.current.analyserInput, 0);
                audioEnvRef.current.inputs = {};

                safeStorage.setItem("faust_editor_version", VERSION);
                dispatch({ type: "SET_OUTPUT_ENABLED", value: true });
                dispatch({ type: "SET_READY" });
            } catch (e) {
                console.error("Failed to initialize Faust:", e);
                toast.error("Failed to initialize Faust", {
                    description: String(e),
                });
            }
        })();
    }, [dispatch, compilerRef, libFaustRef, svgDiagramsRef, bfsRef, compileOptionsRef, audioEnvRef]);
}
